import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "panel",
    "form",
    "searchInput",
    "categoryButton",
    "categoryContent",
    "filterItem",
    "allSitesCheckbox",
    "allEmployeesCheckbox",
    "allTaskTypesCheckbox",
    "allSitesHidden",
    "allEmployeesHidden",
    "allTaskTypesHidden",
    "siteCheckbox",
    "employeeCheckbox",
    "taskTypeCheckbox",
  ]

  connect() {
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.boundHandleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.boundHandleClickOutside)
  }

  toggle(event) {
    event.stopPropagation()
    this.panelTarget.classList.toggle("hidden")
  }

  close() {
    this.panelTarget.classList.add("hidden")
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  selectCategory(event) {
    const category = event.currentTarget.dataset.category

    // Update button styles
    this.categoryButtonTargets.forEach((button) => {
      if (button.dataset.category === category) {
        button.classList.remove("text-gray-600", "hover:bg-gray-100")
        button.classList.add("bg-gray-100", "text-gray-900", "font-medium")
      } else {
        button.classList.remove("bg-gray-100", "text-gray-900", "font-medium")
        button.classList.add("text-gray-600", "hover:bg-gray-100")
      }
    })

    // Show/hide category content
    this.categoryContentTargets.forEach((content) => {
      if (content.dataset.category === category) {
        content.classList.remove("hidden")
      } else {
        content.classList.add("hidden")
      }
    })

    // Clear search input when switching categories
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
      this.filterItemTargets.forEach((item) => item.classList.remove("hidden"))
    }
  }

  search(event) {
    const query = event.target.value.toLowerCase().trim()

    this.filterItemTargets.forEach((item) => {
      const searchText = item.dataset.searchText || ""
      const isVisible = !item
        .closest('[data-board-filter-target="categoryContent"]')
        .classList.contains("hidden")

      if (isVisible) {
        if (query === "" || searchText.includes(query)) {
          item.classList.remove("hidden")
        } else {
          item.classList.add("hidden")
        }
      }
    })
  }

  toggleAll(event) {
    const isChecked = event.target.checked
    const checkbox = event.target

    if (checkbox === this.allSitesCheckboxTarget) {
      this.siteCheckboxTargets.forEach((cb) => {
        cb.checked = false
      })
      if (this.hasAllSitesHiddenTarget) {
        this.allSitesHiddenTarget.value = isChecked ? "1" : "0"
      }
    } else if (checkbox === this.allEmployeesCheckboxTarget) {
      this.employeeCheckboxTargets.forEach((cb) => {
        cb.checked = false
      })
      if (this.hasAllEmployeesHiddenTarget) {
        this.allEmployeesHiddenTarget.value = isChecked ? "1" : "0"
      }
    } else if (checkbox === this.allTaskTypesCheckboxTarget) {
      this.taskTypeCheckboxTargets.forEach((cb) => {
        cb.checked = false
      })
      if (this.hasAllTaskTypesHiddenTarget) {
        this.allTaskTypesHiddenTarget.value = isChecked ? "1" : "0"
      }
    }
  }

  updateSelection(event) {
    const checkbox = event.target

    // If any individual checkbox is checked, uncheck the "all" checkbox
    if (this.hasSiteCheckboxTarget && this.siteCheckboxTargets.includes(checkbox)) {
      const anyChecked = this.siteCheckboxTargets.some((cb) => cb.checked)
      if (this.hasAllSitesCheckboxTarget) {
        this.allSitesCheckboxTarget.checked = !anyChecked
      }
      if (this.hasAllSitesHiddenTarget) {
        this.allSitesHiddenTarget.value = anyChecked ? "0" : "1"
      }
    } else if (this.hasEmployeeCheckboxTarget && this.employeeCheckboxTargets.includes(checkbox)) {
      const anyChecked = this.employeeCheckboxTargets.some((cb) => cb.checked)
      if (this.hasAllEmployeesCheckboxTarget) {
        this.allEmployeesCheckboxTarget.checked = !anyChecked
      }
      if (this.hasAllEmployeesHiddenTarget) {
        this.allEmployeesHiddenTarget.value = anyChecked ? "0" : "1"
      }
    } else if (this.hasTaskTypeCheckboxTarget && this.taskTypeCheckboxTargets.includes(checkbox)) {
      const anyChecked = this.taskTypeCheckboxTargets.some((cb) => cb.checked)
      if (this.hasAllTaskTypesCheckboxTarget) {
        this.allTaskTypesCheckboxTarget.checked = !anyChecked
      }
      if (this.hasAllTaskTypesHiddenTarget) {
        this.allTaskTypesHiddenTarget.value = anyChecked ? "0" : "1"
      }
    }
  }

  clearAll(event) {
    event.preventDefault()

    // Check all "all" checkboxes
    if (this.hasAllSitesCheckboxTarget) {
      this.allSitesCheckboxTarget.checked = true
    }
    if (this.hasAllEmployeesCheckboxTarget) {
      this.allEmployeesCheckboxTarget.checked = true
    }
    if (this.hasAllTaskTypesCheckboxTarget) {
      this.allTaskTypesCheckboxTarget.checked = true
    }

    // Reset hidden fields to indicate "all" is selected
    if (this.hasAllSitesHiddenTarget) {
      this.allSitesHiddenTarget.value = "1"
    }
    if (this.hasAllEmployeesHiddenTarget) {
      this.allEmployeesHiddenTarget.value = "1"
    }
    if (this.hasAllTaskTypesHiddenTarget) {
      this.allTaskTypesHiddenTarget.value = "1"
    }

    // Uncheck all individual checkboxes
    this.siteCheckboxTargets.forEach((cb) => (cb.checked = false))
    this.employeeCheckboxTargets.forEach((cb) => (cb.checked = false))
    this.taskTypeCheckboxTargets.forEach((cb) => (cb.checked = false))

    // Check the with_employees checkbox
    const withEmployeesCheckbox = this.element.querySelector('input[name="with_employees"]')
    if (withEmployeesCheckbox) {
      withEmployeesCheckbox.checked = true
    }

    // Submit the form to apply the cleared filters
    if (this.hasFormTarget) {
      this.formTarget.submit()
    }
  }
}
